import { Domain } from "./types";
import { PracticalExample } from "./types";

export const domains: Domain[] = [
  {
    id: "meaningful-work",
    title: "Meaningful Work",
    shortDescription: "Foundation of success through clear roles and purpose",
    isPrimary: true,
    iconId: "work",
    color: "#3c82f6",
    mainIdea: `Meaningful work is the foundation of individual and organizational success. It begins with clarity—helping individuals understand their roles, responsibilities, and contributions. When leaders provide clear goals, purpose, and growth opportunities, work transforms from tasks into a pathway for fulfillment, progress, and achievement.`,
    keyPoints: [
      "Clear communication of roles and responsibilities",
      "Regular performance feedback cycles - positive and negative",
      "Clear success metrics for each role and how it is measured",
      "Alignment between individual contributions and organizational goals",
    ],
    leadershipActions: [
      `Do my team members clearly understand their roles, responsibilities, and how success is measured?`,
      `Am I providing frequent, balanced (positive and negative) feedback to guide performance?`,
      `Are individuals well-matched to their roles, or do I need to make timely changes to help them thrive?`,
    ],
    practicalExamples: [
      "Regular one-on-one meetings focused on role clarity and growth",
      "Structured feedback sessions with actionable insights",
      "Career development planning sessions",
      "Skills assessment and training programs",
    ],
  },
  {
    id: "spirituality",
    title: "Clinical Spirituality",
    shortDescription: "Purpose beyond self through organizational mission and values",
    isPrimary: true,
    iconId: "sparkles",
    color: "#3c82f6",
    mainIdea:
      "Spirituality is defined clinically as an awareness of something greater than oneself, not confined to organized religion or dogmatic beliefs. It expands one's sense of purpose by connecting individuals to their roles, the organization, and humanity as a whole. Clinical spirituality at work stems from a shared understanding of why the team's mission matters and how well the organization's behaviors and operations align with its values.",
    keyPoints: [
      "Awareness of something greater than oneself",
      "Connection to organizational mission",
      "Alignment of personal and company values",
      "Sense of purpose in daily work",
    ],
    leadershipActions: [
      `Is our mission clearly communicated so employees see how their work contributes?`,
      `Are values authentically practiced, ensuring no gap between what we say and do?`,
      `How do I show each person how their role supports our overarching purpose?`,
    ],
    practicalExamples: ["Regular mission-focused team discussions", "Community service initiatives", "Value-alignment workshops", "Recognition of purpose-driven achievements"],
  },
  {
    id: "relationships",
    title: "SPARC Relationships",
    shortDescription: "Building stress-positive, performance-based connections",
    isPrimary: true,
    iconId: "users",
    color: "#3c82f6",
    mainIdea:
      "Eustress (positive stress) based relationships are characterized by constant communication, high accountability, and elite collaborative performance, combined with the commitment to ongoing improvement. We call these relationships SPARC relationships, meaning they are Stress-inducing, Performance-based, Accountability-centric, Result-oriented, and committed to Continuous improvement.  When the workplace is filled with SPARC relationships, stress is self-induced and positive. These SPARC relationships bring out abilities and capacities in individual team members that are otherwise unattainable alone.",
    keyPoints: [
      "High-performing teams thrive on positive stress through constant communication and mutual accountability",
      "SPARC relationships (Stress-positive, Performance-based, Accountability-centric, Result-oriented, Continuous improvement) drive excellence",
      "Self-induced stress in collaborative environments leads to better outcomes than working alone",
      "Strong workplace relationships unlock individual potential beyond what's achievable independently"
    ],
    leadershipActions: [
      "Am I actively building SPARC relationships where stress drives performance rather than creates distress?",
      "Do my teams have clear accountability structures and metrics that encourage mutual support?",
      "How effectively am I using real-time feedback to accelerate both individual and team performance?",
      "Are we creating an environment where open communication leads to positive stress, growth, and accountability?",
      "Do I model the power of teamwork by demonstrating how collective achievement surpasses individual effort?",
      `Do leaders model strong teamwork and encourage mutual accountability?`,
    ],
    practicalExamples: [
      "Team-building activities focused on performance goals",
      "Collaborative project challenges",
      "Regular feedback and accountability sessions",
      "Cross-functional team initiatives",
    ],
  },
  {
    id: "hobbies",
    title: "Hobbies & Interests",
    shortDescription: "Balancing work with personal growth and stress relief",
    isPrimary: false,
    iconId: "puzzle",
    color: "#3c82f6",
    mainIdea: `When individuals engage in hobbies and interests, it promotes curiosity and independent learning, which offsets routine life stress. Additionally, the freedom to engage in hobbies and activities outside of work is proven to increase an individual's tolerance of stress in pursuit of life goals. Therefore, leaders who support and take an interest in employee hobbies and interests outside of work or highlight such activities in the workplace drive higher Strata Intel (SI) values and help increase the team's capacity to sustain peak performance. A leader's focus on the hobbies and interests domain cannot replace the primary responsibility to create meaningful work, clinical Spirituality, and SPARC relationships at work. While a leader's influence on this domain is secondary, this domain is no less important than the other domains.`,
    keyPoints: [
      "Pursuing personal interests builds resilience and increases stress tolerance in all areas of life",
      "Hobbies foster curiosity and independent learning that enhance workplace performance",
      "Supporting employee interests outside work leads to sustained peak performance at work",
      "While secondary to core workplace domains, hobbies play a vital role in overall stress management"
    ],
    leadershipActions: [
      `Do team schedules allow enough predictability for personal pursuits outside work?`,
      `Am I aware of employees' outside passions and occasionally celebrating them?`,
      `Do I encourage activities that help individuals recharge and maintain a healthy work-life balance?`,
    ],
    practicalExamples: ["Employee interest groups and clubs", "Flexible scheduling to accommodate hobbies", "Sharing personal achievements and interests", "Team building through shared activities"],
  },
  {
    id: "physical-health",
    title: "Physical Health",
    shortDescription: "Supporting wellness through predictable schedules and healthy practices",
    isPrimary: false,
    iconId: "heart",
    color: "#3c82f6",
    mainIdea:
      `When individuals engage in good physical health routines, they manage the effects of life stress more efficiently than by any other means. Leaders who understand the crucial role of physical health create predictable schedules so that individual employees can reliably access windows of opportunity to engage in good physical health routines. Encouraging good physical health routines for your employees outside of work mitigates stress and increases personal capacity to perform sequential tasks with precision and accuracy. Prioritizing regular physical activity benefits the individual, directly improving overall health, boosting energy, and supporting a longer, more vibrant life.`,
    keyPoints: [
      "Physical health routines are the most effective way to manage life stress",
      "Predictable schedules enable employees to maintain consistent health practices",
      "Regular physical activity improves task precision and sustained performance",
      "Supporting employee health habits leads to higher energy and long-term vitality"
    ],
    leadershipActions: [
      `Do schedules honor the need for adequate sleep, exercise, and nutrition?`,
      `Are we consistent in discouraging off-hours work that undermines personal well-being?`,
      `Do I communicate how healthy habits directly support performance?`
    ],
    practicalExamples: ["Flexible scheduling for exercise", "Wellness program initiatives", "Health education resources", "Stress management workshops"],
  },
];

export const practicalExamples: Record<string, PracticalExample[]> = {
  "meaningful-work": [
    {
      scenario: "Role Clarity in Therapy Department",
      challenge: "Overlapping responsibilities between PT/OT assistants and technicians causing confusion and inefficiency",
      solution: "Implement role-specific task matrices and daily huddle boards. Create visual workflow charts showing clear handoff points between roles.",
      outcomes: ["Reduction in task duplication", "Improved staff confidence in role boundaries", "Clearer career progression paths", "Higher patient satisfaction with therapy coordination", "Staff retention - understanding roles and responsibilities"],
    },
  ],
  spirituality: [
    {
      scenario: "Connecting Daily Tasks to Patient Impact",
      challenge: "Staff feeling disconnected from the mission while performing routine tasks",
      solution: "Implement 'Patient Journey Maps' showing how each role contributes to recovery. Share weekly patient success stories in team meetings.",
      outcomes: ["Increased employee engagement scores", "Higher reported job satisfaction", "Improved quality measures", "Better patient experience ratings"],
    },
  ],
  relationships: [
    {
      scenario: "Building High-Performance Care Teams",
      challenge: "Siloed working patterns between nursing, therapy, and social services affecting patient outcomes",
      solution: "Create cross-functional care teams with daily SPARC huddles. Implement peer accountability partners across departments.",
      outcomes: ["Improvement in care plan completion", "Reduced length of stay", "Better interdepartmental communication", "Increased team problem-solving capability"],
    },
  ],
  hobbies: [
    {
      scenario: "Staff Burnout Prevention",
      challenge: "High stress levels leading to decreased engagement and increased callouts",
      solution: "Launch 'Interests Initiative': flexible scheduling for personal activities, department hobby clubs, and skill-sharing sessions.",
      outcomes: ["Reduction in sick calls", "Improved work-life balance", "Enhanced team bonding", "Lower reported stress levels"],
    },
  ],
  "physical-health": [
    {
      scenario: "Night Shift Wellness Program",
      challenge: "Night shift staff struggling with sleep patterns and healthy habits",
      solution: "Implement 'Night Owl Wellness': scheduled break rotations, healthy snack options, and sleep hygiene education.",
      outcomes: ["Improved staff alertness", "Better sleep quality reports", "Reduced medication errors", "Higher staff retention rates"],
    },
  ],
};
